
import { MealCompositionWithRecipes, DbMealComposition } from "@/types/mealComposition";
import { supabase } from "@/integrations/supabase/client";
import { withErrorHandling } from "../utils/errorHandling";
import { 
  mapDbMealCompositionToMealComposition, 
  populateCompositionWithRecipes 
} from "./mealCompositionUtils";
import { recipeService } from "@/services/recipe";

/**
 * Service for managing recipes within meal compositions
 */
export const mealCompositionRecipeService = {
  /**
   * Add a recipe to an existing meal composition
   */
  addRecipeToComposition: withErrorHandling(
    async (compositionId: string, recipeId: string): Promise<MealCompositionWithRecipes | null> => {
      // First, get the current composition
      const { data: currentComposition, error: fetchError } = await supabase
        .from("meal_compositions")
        .select("*")
        .eq("id", compositionId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (!currentComposition) {
        throw new Error("Meal composition not found");
      }

      // Add the recipe ID if it's not already in the array
      const currentRecipeIds = currentComposition.recipe_ids || [];
      if (!currentRecipeIds.includes(recipeId)) {
        const updatedRecipeIds = [...currentRecipeIds, recipeId];

        const { data, error: updateError } = await supabase
          .from("meal_compositions")
          .update({ 
            recipe_ids: updatedRecipeIds,
            updated_at: new Date().toISOString()
          })
          .eq("id", compositionId)
          .select()
          .single();

        if (updateError) {
          throw updateError;
        }

        // Convert from database format
        const updatedComposition = mapDbMealCompositionToMealComposition(data as DbMealComposition);

        // Fetch recipes to populate the composition
        const recipes = await recipeService.getRecipes();

        // Return populated composition
        return populateCompositionWithRecipes(updatedComposition, recipes);
      }

      // Recipe already exists, return current composition
      const composition = mapDbMealCompositionToMealComposition(currentComposition as DbMealComposition);
      const recipes = await recipeService.getRecipes();
      return populateCompositionWithRecipes(composition, recipes);
    },
    "Error adding recipe to meal composition"
  ),

  /**
   * Remove a recipe from an existing meal composition
   */
  removeRecipeFromComposition: withErrorHandling(
    async (compositionId: string, recipeId: string): Promise<MealCompositionWithRecipes | null> => {
      // First, get the current composition
      const { data: currentComposition, error: fetchError } = await supabase
        .from("meal_compositions")
        .select("*")
        .eq("id", compositionId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (!currentComposition) {
        throw new Error("Meal composition not found");
      }

      // Remove the recipe ID from the array
      const currentRecipeIds = currentComposition.recipe_ids || [];
      const updatedRecipeIds = currentRecipeIds.filter(id => id !== recipeId);

      const { data, error: updateError } = await supabase
        .from("meal_compositions")
        .update({ 
          recipe_ids: updatedRecipeIds,
          updated_at: new Date().toISOString()
        })
        .eq("id", compositionId)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      // Convert from database format
      const updatedComposition = mapDbMealCompositionToMealComposition(data as DbMealComposition);

      // Fetch recipes to populate the composition
      const recipes = await recipeService.getRecipes();

      // Return populated composition
      return populateCompositionWithRecipes(updatedComposition, recipes);
    },
    "Error removing recipe from meal composition"
  )
};
